import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Loader, ChevronDown, ChevronUp, Download, Image as ImageIcon, File, X, ExternalLink } from 'lucide-react';

interface Consultation {
  id: string;
  date: string;
  symptoms: string[];
  diagnosis: string | null; // Legacy field - may contain old combined notes
  internalNotes?: string | null; // NEW: Internal doctor notes (private) - from backend
  patientRecommendation?: string | null; // NEW: Patient-facing recommendations - from backend
  doctorName: string;
  status: string;
  callDuration?: number;
}

interface Attachment {
  id: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  url: string;
  requestId: string;
  description?: string;
}

interface PatientConsultationHistoryProps {
  patientEmail: string;
}

const PatientConsultationHistory: React.FC<PatientConsultationHistoryProps> = ({ patientEmail }) => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedConsultation, setExpandedConsultation] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null);

  useEffect(() => {
    fetchPatientHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientEmail]);

  const fetchPatientHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      
      const historyResponse = await fetch(
        `${apiUrl}/patients/history?gmail=${encodeURIComponent(patientEmail)}`
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setConsultations(historyData.consultations || []);
      } else if (historyResponse.status === 404) {
        setConsultations([]);
      } else {
        throw new Error('Failed to load consultation history');
      }

      const filesResponse = await fetch(
        `${apiUrl}/patients/files?gmail=${encodeURIComponent(patientEmail)}`
      );

      if (filesResponse.ok) {
        const filesData = await filesResponse.json();
        setFiles(filesData.files || []);
      } else if (filesResponse.status === 404) {
        setFiles([]);
      } else {
        console.warn('Failed to load patient files');
        setFiles([]);
      }
    } catch (err) {
      console.error('Error fetching patient history:', err);
      setError('Failed to load patient history');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="w-4 h-4 text-blue-600" />;
    if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-600" />;
    return <File className="w-4 h-4 text-gray-600" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFilesForConsultation = (consultationId: string) => {
    return files.filter(file => file.requestId === consultationId);
  };

  const handlePreview = (file: Attachment) => {
    console.log('🖼️ Opening preview for:', file.filename);
    console.log('📎 File URL:', file.url);
    console.log('📋 File type:', file.mimeType);
    setPreviewFile(file);
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');
  const isPDF = (mimeType: string) => mimeType === 'application/pdf';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader className="w-5 h-5 animate-spin text-blue-600" />
        <span className="ml-2 text-sm text-gray-600">Loading patient history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">{error}</p>
      </div>
    );
  }

  if (consultations.length === 0 && files.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">No previous consultation history</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {consultations.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Previous Consultations ({consultations.length})
            </h4>
            <div className="space-y-2">
              {consultations.map((consultation) => {
                const isExpanded = expandedConsultation === consultation.id;
                const consultationFiles = getFilesForConsultation(consultation.id);

                return (
                  <div key={consultation.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedConsultation(isExpanded ? null : consultation.id)}
                      className="w-full p-4 bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-sm font-medium text-gray-900">{formatDate(consultation.date)}</span>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">#{consultation.id}</span>
                          {consultation.callDuration && <span className="text-xs text-gray-600">{consultation.callDuration} min</span>}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {consultation.symptoms.slice(0, 3).map((symptom, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">{symptom}</span>
                          ))}
                          {consultation.symptoms.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">+{consultation.symptoms.length - 3}</span>
                          )}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                    </button>

                    {isExpanded && (
                      <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                        {consultation.doctorName && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Consulted with</p>
                            <p className="text-sm font-medium text-gray-900">{consultation.doctorName}</p>
                          </div>
                        )}

                        <div>
                          <p className="text-xs text-gray-600 mb-1">Symptoms</p>
                          <div className="flex flex-wrap gap-1">
                            {consultation.symptoms.map((symptom, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{symptom}</span>
                            ))}
                          </div>
                        </div>

                        {/* Internal Doctor Notes (Private) - Only visible to doctors */}
                        {consultation.internalNotes && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                              <span>🔒</span> Internal Doctor Notes (Private)
                            </p>
                            <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{consultation.internalNotes}</p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">🔒 Private - Not visible to patient</p>
                          </div>
                        )}

                        {/* Patient Recommendation - What was sent to patient */}
                        {consultation.patientRecommendation && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                              <span>📧</span> Patient Recommendation
                            </p>
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{consultation.patientRecommendation}</p>
                            </div>
                            <p className="text-xs text-blue-600 mt-1">📧 This was sent to the patient</p>
                          </div>
                        )}

                        {/* Legacy diagnosis field - for old consultations before the split */}
                        {consultation.diagnosis && !consultation.internalNotes && !consultation.patientRecommendation && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Medical Notes & Diagnosis</p>
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{consultation.diagnosis}</p>
                            </div>
                          </div>
                        )}

                        {consultationFiles.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-600 mb-2">Attachments ({consultationFiles.length})</p>
                            <div className="space-y-2">
                              {consultationFiles.map((file) => (
                                <div key={file.id} className="flex items-center gap-3 p-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                                  <div className="flex-shrink-0">{getFileIcon(file.mimeType)}</div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-900 truncate">{file.filename}</p>
                                    <p className="text-xs text-gray-600">{formatFileSize(file.fileSize)}</p>
                                  </div>
                                  <div className="flex gap-1">
                                    {(isImage(file.mimeType) || isPDF(file.mimeType)) && (
                                      <button onClick={() => handlePreview(file)} className="flex-shrink-0 p-1.5 hover:bg-blue-200 rounded transition-colors" title="Preview file">
                                        <ExternalLink className="w-4 h-4 text-blue-600" />
                                      </button>
                                    )}
                                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 p-1.5 hover:bg-blue-200 rounded transition-colors" title="Download file">
                                      <Download className="w-4 h-4 text-blue-600" />
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {files.filter(f => !f.requestId || !consultations.find(c => c.id === f.requestId)).length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Additional Files (Not Linked to Specific Consultation)
            </h4>
            <div className="space-y-2">
              {files.filter(f => !f.requestId || !consultations.find(c => c.id === f.requestId)).map((file) => (
                <div key={file.id} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                  <div className="flex-shrink-0">{getFileIcon(file.mimeType)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                    <p className="text-xs text-gray-600">{formatFileSize(file.fileSize)} • {formatDate(file.uploadedAt)}</p>
                  </div>
                  <div className="flex gap-1">
                    {(isImage(file.mimeType) || isPDF(file.mimeType)) && (
                      <button onClick={() => handlePreview(file)} className="flex-shrink-0 p-2 hover:bg-blue-200 rounded-lg transition-colors" title="Preview file">
                        <ExternalLink className="w-4 h-4 text-blue-600" />
                      </button>
                    )}
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 p-2 hover:bg-blue-200 rounded-lg transition-colors" title="Download file">
                      <Download className="w-4 h-4 text-blue-600" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{previewFile.filename}</h3>
                <p className="text-sm text-gray-600">{formatFileSize(previewFile.fileSize)} • {formatDate(previewFile.uploadedAt)}</p>
              </div>
              <div className="flex gap-2 ml-4">
                <a href={previewFile.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Open in new tab">
                  <ExternalLink className="w-5 h-5 text-gray-600" />
                </a>
                <a href={previewFile.url} download={previewFile.filename} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Download">
                  <Download className="w-5 h-5 text-gray-600" />
                </a>
                <button onClick={() => setPreviewFile(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {isImage(previewFile.mimeType) ? (
                <div className="flex items-center justify-center h-full">
                  <img 
                    src={previewFile.url} 
                    alt={previewFile.filename} 
                    className="max-w-full max-h-full object-contain" 
                    onError={(e) => {
                      console.error('❌ Image load error:', previewFile.url);
                      console.error('File details:', previewFile);
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                      const parent = img.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="text-center p-8">
                            <div class="text-red-600 mb-4">
                              <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p class="text-lg font-semibold mb-2">Failed to Load Image</p>
                              <p class="text-sm text-gray-600 mb-4">The image URL may have expired or CORS is not configured</p>
                            </div>
                            <div class="text-left bg-gray-100 p-4 rounded-lg mb-4">
                              <p class="text-xs font-mono text-gray-700 break-all">${previewFile.url.substring(0, 100)}...</p>
                            </div>
                            <p class="text-sm text-gray-600 mb-4">
                              <strong>Possible causes:</strong><br/>
                              • S3 presigned URL has expired (backend needs longer expiration)<br/>
                              • S3 bucket CORS not configured<br/>
                              • File permissions issue
                            </p>
                            <a href="${previewFile.url}" target="_blank" class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                              Try Opening in New Tab
                            </a>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              ) : isPDF(previewFile.mimeType) ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-full min-h-[600px] border-0"
                  title={previewFile.filename}
                  onError={() => {
                    console.error('❌ PDF load error:', previewFile.url);
                    alert('Failed to load PDF. The file may have expired or you may not have permission to view it.\n\nTry opening in a new tab or contact support.');
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                    <a
                      href={previewFile.url}
                      download={previewFile.filename}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientConsultationHistory;
